import {SchemaTag} from 'folder-items/FileConstants';
import {EntrySummary} from 'notebook/ELNConstants';
import {AssayResult} from 'results/assays/AssayRecordConstants';
import {StageEntrySummary} from 'workflows/WorkflowConstants';

export enum LookupType {
  SAMPLE = 'SAMPLE',
  STAGE_RUN_INPUT = 'STAGE_RUN_INPUT',
  EXPERIMENTAL_CONDITION = 'EXPERIMENTAL_CONDITION',
}
// Standard order of lookup types
export const LOOKUP_TYPES = [
  LookupType.SAMPLE,
  LookupType.STAGE_RUN_INPUT,
  LookupType.EXPERIMENTAL_CONDITION,
];

export interface FilterSpecifier {
  fieldFilter: {
    name: string | null;
    value: string | null;
  };
}

export interface BaseLookupDefinition {
  schemaName: string | null;
  schemaId?: string | null;
  fieldName: string | null;
  fieldType?: string | null;
  onlyValid?: boolean | null;
  filterSpecifiers?: Array<FilterSpecifier> | null;
}

// The sample on which the lookup field is performed
export interface SampleSpecifier {
  fileId?: string | null;
  batchId?: string | null;
  stageRunSampleId?: string | null;
  stageRunId?: string | null;
}

// Lookup Specifier with the sourceSpecifier replaced with the resolved SampleSpecifier
export interface LookupFieldDefinition extends BaseLookupDefinition {
  sampleSpecifier: SampleSpecifier | null;
}

export type StageRunSpecifierFieldType =
  | 'EXPERIMENTAL_CONDITION'
  | 'INPUT_SAMPLE_BATCH'
  | 'INPUT_SAMPLE_ENTITY'
  | 'INPUT_SAMPLE_BATCH_ENTITY'
  | 'SAMPLE_BATCH'
  | 'SAMPLE_ENTITY'
  | 'SAMPLE_BATCH_ENTITY';
export interface InputSampleLookupSpecifier extends BaseLookupDefinition {
  fieldType: StageRunSpecifierFieldType;
  stageRunId: string | null;
  stageRunSampleId: string | null;
  sourceSpecifier: null;
}

// Optional argument in Lookup Specifier that indicates that the sample (or stage run) to query is not the
// main sample (or stage run) of the row, but defined by a nested lookup field query instead.
// Parsed from: `LOOKUPFIELD(x,t,z,"SOURCE",LOOKUPFIELD(...))` and may be nested
export interface SourceSpecifier {
  lookupSpecifier: {
    schemaName: string | null;
    schemaId?: string | null;
    fieldName: string | null;
    fieldType?: string | null;
    sourceSpecifier?: SourceSpecifier | null;
  };
}

// Representation of the lookup field formula `LOOKUPFIELD(x,t,z,”SOURCE”,LOOKUPFIELD(...))`
// The format of lookup field specifier that is sent and received from the server.
export interface LookupSpecifier extends BaseLookupDefinition {
  sourceSpecifier?: SourceSpecifier | null;
}

// Result for a LOOKUPFIELD query of a single cell as it is cached in the table cell
export interface LookupFieldResult {
  assayResultSummary?: {
    id: string;
    createdAt: string;
  };
  entry?: StageEntrySummary;
  value: number | string;
  tagValue: SchemaTag | null;
  type: string | null; // Not required for backwards compatibility
}

export interface SubqueryLookupResultValue {
  assayResultSummary?: AssayResult | null;
  entry?: EntrySummary | null;
  tag: SchemaTag;
}

export interface SubqueryLookupResult {
  lookupSpecifier: LookupSpecifier;
  sampleSpecifier: SampleSpecifier;
  values: Array<SubqueryLookupResultValue | string>;
}

// The shape of the query for an individual lookupSpecifier and individual sampleSpecifier.
export type ServerLookupQuery = LookupSpecifier & SampleSpecifier;

// The shape of the result for an individual lookupSpecifier as it is returned by the server.
// If the query is nested, the array would contain a result for each individual sub-query.
// A single ServerLookupResult corresponds to a single ServerLookupQuery.
export type ServerLookupResult = Array<SubqueryLookupResult>;

// The shape a server request for multiple lookup fields queries performed in bulk.
// For example when the entire table is refreshed, there will be an individual ServerLookupQuery per
// table cell, and the top level server query would be ServerLookupAggregatedQuery.
export type ServerLookupAggregatedQuery = Array<ServerLookupQuery>;

// The shape of the aggregated results for multiple lookup fields queries performed in bulk.
// For example when the entire table is refreshed, there will be an individual ServerLookupResult per
// table cell, and the server response for the bulk query would contain ServerLookupAggregatedResult.
// ServerLookupAggregatedResult is the server response for a ServerLookupAggregatedQuery
export type ServerLookupAggregatedResult = Array<ServerLookupResult>;
